import { createStore } from "vuex";
import VuexPersist from 'vuex-persist'
window.g25cache = window.g25cache || {}
import genoVersion from './genoVersion.json'
const genoVer = genoVersion.version
import jsonstrings from './jsonstrings.json'

// ─── Durable UI preferences ─────────────────────────────────────
// A registry of user preferences that should survive tab close —
// the session-scoped vuex-persist (below) isn't enough for "advanced
// users who turned this feature off and never want to see it again".
// We keep these in localStorage, seed Vuex state at boot, exclude
// them from the session snapshot (so localStorage stays the source
// of truth), and write-through on mutation via a subscribe hook.
//
// Each entry declares:
//   scope:   'global' — one value shared across every user on this
//                       device (e.g. UI-chrome preferences)
//            'user'   — scoped to the logged-in user's username;
//                       re-hydrates on sign-in / sign-out / user
//                       switch. Falls back to `default` when no user.
//   default: value returned when localStorage has no entry yet.
//
// Values are JSON-serialized, so booleans/strings/numbers/objects all
// round-trip cleanly. Existing bool entries stored as '0' / '1' by
// earlier code remain readable because both parse as valid JSON.
//
// Storage keys:
//   global  → `genoplot.prefs.<key>`
//   user    → `genoplot.prefs.u.<username>.<key>`
const PREFS_PREFIX = 'genoplot.prefs.'
const PERSISTED_PREFS = {
    showStarterStrip: { scope: 'global', default: true },
    imputes: { scope: 'user', default: 0 },
    // Individuals switch on formalstats (target / source / outgroup
    // pickers). User-scoped so advanced users who leave it on get
    // that state restored on every sign-in, while fresh accounts
    // start with the safer whole-pops default.
    allowIndividuals: { scope: 'user', default: false },

    // ── formalstats per-tool advanced configs ─────────────────────────
    // User-scoped so each account's tuned knobs (Allsnps, rotation
    // priority, max sources, p-value cutoff, transform choice, …)
    // survive sign-out and re-hydrate on sign-in. Object defaults
    // are reused by formalstats.vue.js's setup so changing them in
    // one place updates both the boot-seed and the page-init values.
    formalstatsActiveTool: { scope: 'user', default: 'qpAdm' },
    formalstatsSwitches: {
        scope: 'user',
        default: { scaled: true, autoRotate: true, useTransitions: true }
    },
    qpAdmConfig: {
        scope: 'user',
        default: {
            allsnps: true, rotationPriority: 'left', details: true,
            maxSources: 5, minPValue: 0.05
        }
    },
    qpWaveConfig: {
        scope: 'user',
        default: { maxRank: 10, allsnps: true }
    },
    fStatConfig: {
        scope: 'user',
        default: { inbreed: false, f4mode: true, allsnps: true }
    },
    qpGraphConfig: {
        scope: 'user',
        default: { numAdmix: 0, outpop: null, useScale: true, maxIterations: 100 }
    },
    fstConfig: {
        scope: 'user',
        default: { allsnps: true, projection: 'pcoa', projection_transform: 'raw' }
    }
}
const PERSISTED_KEYS = Object.keys(PERSISTED_PREFS)

// Forward declaration — `currentUserId` reads from the store, but the
// store hasn't been created at module-init time when `readPref` first
// runs for the initial seed. We lazily bind after `store` exists.
let _store = null

function currentUserId() {
    try {
        const u = _store && _store.state && _store.state.user
        return (u && u.username) ? u.username : null
    } catch (_) {
        return null
    }
}

// Resolve the localStorage key for a pref. Returns null for
// user-scoped prefs when no user is logged in — callers treat null
// as "fall back to default, don't touch storage".
function prefStorageKey(key) {
    const cfg = PERSISTED_PREFS[key]
    if (!cfg) return null
    if (cfg.scope === 'user') {
        const uid = currentUserId()
        return uid ? `${PREFS_PREFIX}u.${uid}.${key}` : null
    }
    return PREFS_PREFIX + key
}

function readPref(key) {
    const cfg = PERSISTED_PREFS[key]
    if (!cfg) return undefined
    const storageKey = prefStorageKey(key)
    if (!storageKey) return cfg.default
    try {
        const raw = localStorage.getItem(storageKey)
        if (raw === null) return cfg.default
        return JSON.parse(raw)
    } catch (_) {
        return cfg.default
    }
}

function writePref(key, value) {
    const storageKey = prefStorageKey(key)
    if (!storageKey) return
    try {
        localStorage.setItem(storageKey, JSON.stringify(value))
    } catch (_) {}
}

// Session snapshot — excludes the localStorage-backed prefs so
// those don't get round-tripped through sessionStorage on restore.
// Without this exclusion the session snapshot would win over
// localStorage on boot (plugin runs after initial-state seed).
// Transient slices that should never survive a tab refresh — the
// modal that consumes them re-seeds from the backend on every open,
// and persisting them just risks showing stale state during the brief
// window before the next fetch lands.
const TRANSIENT_KEYS = [
    'imputationJobs',
    // SNP browser mock-mode flag is intentionally session-shaped: stays put
    // while the user toggles it during a session, but every fresh page load
    // resets to the JS default (true) so the dev affordance is always
    // recoverable. Same for the active kit selection — kit_id is a query
    // param on the route, no need to also persist it in the snapshot.
    'snpBrowserMockMode',
    'snpBrowserCurrentKitId'
]

const vuexPersist = new VuexPersist({
    key: 'genoplot-persist',
    storage: sessionStorage,
    reducer: (state) => {
        const snapshot = { ...state }
        for (const key of PERSISTED_KEYS) delete snapshot[key]
        for (const key of TRANSIENT_KEYS) delete snapshot[key]
        return snapshot
    }
})
function deepFreeze(obj) {
  if (typeof obj === 'object' && obj !== null) {
    if (Array.isArray(obj)) {
      obj.forEach(element => {
        if (typeof element === 'object' && element !== null) {
          deepFreeze(element); // Recursively freeze array elements
        }
      });
    } else {
      Object.getOwnPropertyNames(obj).forEach(prop => {
        const value = obj[prop];
        if (typeof value === 'object' && value !== null) {
          deepFreeze(value);
        }
      });
    }
    return Object.freeze(obj);
  }
  return obj;
}

var settings = {
    slider: {
        limit: {
            value: 'OFF',
            data: [
                ['OFF', 3, 4, 5, 6, 7, 8]
            ]
        },
        min: {
            value: '0%',
            data: [
                ['0%', '1%', '2%']
            ]
        },
        batches: {
            value: 200,
            data: [
                [100, 200, 400, 500, 600, 800, 1000],
                [100, 200, 300, 400, 500]
            ]
        },
        cycles: {
            value: 200,
            data: [
                [100, 200, 400, 500, 600, 800, 1000],
                [100, 200, 300, 400, 500]
            ]
        },
        maxfits: {
            value: 20,
            data: [
                [20, 100, 200, 500, 600, 800, 1000],
                [20, 30, 40, 50, 60, 80, 90, 100]
            ]
        },
        colors: {
            value: "1",
            data: ["1", "2", "3", "4", "5"]
        },
        layout: {
            value: "Chart",
            data: ["Chart", "Data", "Both"]
        },
        theme: {
            value: "system",
            data: ["system", "light", "dark"]
        }
    },
    switches: {
        defaultOn: true,
        defaultOff: false,
        scaled: true,
        paidPlan: true,
        calculator: false,
        basePlan: false,
        custom: true,
        editPCA: false,
        ancient: false,
        removeOutliers: false,
        penalty: true,
        groupAveraged: false,
        groupMedian: false,
        optimaFit: false,
        groupSamples: false,
        lite: false,
    }
}
var data = {
    labsPrimaryTab: 'maps',
    frontpageLoaded: false,
    mfalogin: false,
    scriptLoaded: false,
    mfaUser: null,
    cached: Date.now(),
    simMaps: {},
    groupedSamples: true,
    g25Loaded: false,
    pcaLoaded: false,
    admixLoaded: false,
    fstatsLoaded: false,
    customCSV: '',
    genod: false,
    G25calc: false,
    intercomhash: false,
    discussLoaded: false,
    navLoaded: false,
    imageSource: '',
    contacted: false,
    dftemp: false,
    dfsid: false,
    alias: false,
    genolabs: false,
    showlab: false,
    avatarImage: false,
    settings: settings,
    authState: 'signIn',
    netError: false,
    userdata: false,
    showshare: false,
    proxyUser: false,
    sharedurl: false,
    // History-modal swiper context. Populated when the user opens a
    // share view via the history modal — carries the ordered list of
    // currently-visible history URLs plus the index of the one they
    // clicked. Nav's share drawer reads this and renders a Swiper of
    // slides (one ShareView per URL) so users can swipe through their
    // history without re-opening the modal. Cleared on drawer close.
    // Direct `/shared/...` URL hits never populate this — those keep
    // the single-artifact behavior.
    historyShareList: false,
    xflipped: false,
    yflipped: false,
    genoVer: genoVer,
    isMobile: false,
    topClass: false,
    customgroups: [],
    peeruser: false,
    subscribed: false,
    activeModal: '',
    activePage: 'landing',
    annotationText: '',
    searchPCA: '',
    g25firstRun: true,
    graphQLurl: false,
    primaryTabs: "",
    usergroups: [],
    isAdmin: false,
    secondaryTabs: "",
    onLine: true,
    activePane: 'chart',
    checkFitsData: [],
    changes: {},
    calcChanges: {},
    themes: ['alice', 'jade', 'ruby', 'kelly', 'indigo'],
    credits: 0,
    imputes: readPref('imputes'),
    env: window.location.hostname.split('.')[0] === "staging" ? 'staging' : window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname === '0.0.0.0' || window.location.hostname == 'dev.genoplot.com' || window.location.hostname == '192.168.86.39' || window.location.hostname.substr(0,3) == '10.' ? 'development' : 'production',
    currentIndex: 0,
    ENTITLEMENTS: null,
    SET_ENTITLEMENTS_READY: false,
    activeCalcIndex: 0,
    activeOwnerIndex: 0,
    subId: false,
    editedImageUrl: '',
    editedImageUrl2: '',
    editingImage: false,
    dataset: "Standard",
    datasets: ["Standard", "High-Res", "Max"], 
    statusMessage: '',
    g25Rerun: false,
    pcaRerun: false,
    // SNP browser feature — mock mode is on while backend is being built.
    // Toggleable in-app via the page header (visible only with the
    // snpbrowser entitlement). Defaults to true; flip to false once the
    // live AppSync resolvers are wired.
    snpBrowserMockMode: true,
    snpBrowserCurrentKitId: null,
    g25expand: 0,
    theme: false,
    fadeIn: false,
    showGrid: 0,
    chartType: 'radialBar',
    geo: {},
    target: false,
    user: sessionStorage.getItem('user'),
    monteMode: 'G25',
    g25customSamples: [],
    pcacustomSamples: [],
    pcadata: {},
    lastSort: false,
    lastQuery: false,
    spinner: false,
    cbInstance: false,
    subscription: false,
    subsActive: false,
    sampleArray: [],
    rawArray: [],
    progress: 0,
    lastsync: false,
    screenWidth: 1280,
    screenHeight: 1024,
    saveReady: false,
    queryTimeStamp: 0,
    groupArray: [],
    mapSampleArray: [],
    calcGroupsArray: [],
    mapGroupArray: [],
    mapCalcGroupsArray: [],
    mapchartJSON: false,
    mapheatmapJSON: false,
    mapcalcJSON: '',
    mapchartColors: false,
    mapcalcColors: false,
    mapchartCategories: [],
    mapchartLoading: false,
    mapchartRunning: false,
    mapchartSeries: [],
    mapcalcSeries: [],
    mapcalcSampleSeries: [],
    mapcalcSampleComponents: [],
    mapcalcComponents: [],
    mapchartComponents: [],
    calcArray: [],
    g25chartJSON: false,
    g25heatmapJSON: false,
    g25calcJSON: false,
    g25chartColors: false,
    g25calcColors: false,
    g25chartCategories: [],
    g25chartLoading: false,
    g25chartRunning: false,
    g25chartSeries: [],
    g25calcSeries: [],
    g25calcSampleSeries: [],
    g25calcSampleComponents: [],
    g25calcComponents: [],
    g25chartComponents: [],
    rawchartJSON: false,
    rawchartRefs: false,
    rawcalcJSON: false,
    rawchartColors: false,
    rawcalcColors: false,
    rawchartCategories: [],
    rawchartLoading: false,
    rawchartRunning: false,
    rawchartSeries: [],
    rawcalcSeries: [],
    rawcalcSampleSeries: [],
    rawcalcSampleComponents: [],
    rawcalcComponents: [],
    rawchartComponents: [],
    plotSwitches: {
        averaged: false,
        median: false,
        all: true
    },
    plotSurface: false,
    plotMesh: false,
    plotMode: false,
    screenshot: false,
    g25multiview: false,
    pcamultiview: false,
    admixmultiview: false,
    qpmultiview: false,
    g25showlast: false,
    pcashowlast: false,
    admixshowlast: false,
    qpshowlast: false,
    admixTools2: false,
    ahistory: true,
    highres: false,
    autorotate: false,
    plotStyle: {},
    plotSlider: {
        surfaceaxis: {
            value: 'off',
            data: ['off', 'z-axis', 'y-axis', 'x-axis']
        }
    },
    plotUserSamples: [],
    plotGroupSamples: [],
    plotCustomGroups: [],
    plotInterestedSamples: [],
    userSamples: [],
    editSamples: [],
    userCalcs: [],
    editCalcs: [],
    smallScreen: false,
    maxVals: false,
    maxRawVals: false,
    maxRefVals: false,
    g25chartKey: false,
    lastUpdate: false,
    userUpdated: false,
    countdown: false,
    appVisible: false,
    switches: {
        ...settings.switches
    },
    rswitches: {
        ...settings.switches
    },
    slider: {
        ...settings.slider
    },
    authflow: false,
    routeKey: 0,
    fstatsActiveJob: null,
    // ─── Custom GenoEntity state (groups + models) ───────────────
    // Populated by `refreshCustomGroupsState` / `refreshCustomModelsState`
    // in src/services/customGroups.js. Kept at the store level so every
    // page sees the same catalog without re-fetching per mount.
    //
    //   user{Groups,Models}          → user's own entities (full blob,
    //                                  including members / params)
    //   public{Groups,Models}Catalog → public catalog from S3, refreshed
    //                                  when the publisher Lambda broadcasts
    //                                  or when `version.json` changes
    //   editingModel                 → { userid, entityname, name } when
    //                                  a CustomModelsModal → Edit flow
    //                                  has loaded a model's selections
    //                                  into formalstats; null otherwise.
    //                                  Drives the editing banner + the
    //                                  "Save" fast-path (in-place update).
    userGroups: [],
    publicGroupsCatalog: [],
    userModels: [],
    publicModelsCatalog: [],
    editingModel: null,
    // Quick Start results cache — persisted in sessionStorage via
    // vuex-persist so re-opening the results dialog after loading
    // the first suggested model (and iterating) doesn't require
    // another 17-second Lambda round-trip. Shape:
    //   { target: '<sample-name>', body: <Lambda body>, createdAt }
    // Auto-invalidated when the user changes target — a cached
    // result for sample A has no bearing on sample B.
    quickStartResult: null,
    // Optimize Model session — iteration-aware trail of failed
    // model attempts + AI diagnoses, sent with each `recommend_model`
    // call so the Lambda can consider the whole trajectory rather
    // than treating each failed run in isolation. Shape:
    //   { target: '<sample-name>',
    //     history: [{ target, model, failure_mode, diagnosis }, …],
    //     lastBody: <Lambda response body> }
    // Auto-invalidated when target changes (diagnosis trail for
    // sample A isn't useful for sample B).
    optimizeSession: null,
    // Inline starter-models strip visibility on formalstats.
    // Default ON so new users see discovery affordances; advanced
    // users can hide it via the rightpanel toggle or the strip's
    // own "×" icon. Durable via localStorage (see PERSISTED_PREFS
    // above) — advanced users who turn it off once stay off across
    // sessions, devices aside.
    // Durable prefs — seeded from localStorage via readPref. User-
    // scoped prefs return their `default` here (no user at boot) and
    // get re-hydrated once `state.user` is committed (see subscribe
    // hook below).
    showStarterStrip: readPref('showStarterStrip'),
    // Session-local memory of which tab the CustomModelsModal had
    // open. Vuex only (not localStorage-backed) — doesn't need to
    // survive tab-close; just avoids resetting to cascade-default
    // every time the user reopens the modal in the same session.
    lastModelsTab: null,
    // Durable (user-scoped, localStorage) gate for individual-level
    // selection in the Target / Source / Outgroup pickers on
    // formalstats. OFF (default): all three are restricted to whole
    // populations. ON: searches return grouped results (population →
    // individuals) so users can pick either granularity. See
    // `PERSISTED_PREFS` above — value survives sign-out / tab close
    // and is rehydrated when the same user signs back in. "My Samples"
    // target mode and the CustomGroupEditor are NOT affected by this
    // flag — they have their own selection rules.
    allowIndividuals: readPref('allowIndividuals'),
    // formalstats per-tool advanced configs — each lives in Vuex so the
    // page can two-way bind via the existing `change` mutation, and the
    // write-through subscriber persists to user-scoped localStorage. See
    // PERSISTED_PREFS above for object shapes.
    formalstatsActiveTool: readPref('formalstatsActiveTool'),
    formalstatsSwitches: readPref('formalstatsSwitches'),
    qpAdmConfig: readPref('qpAdmConfig'),
    qpWaveConfig: readPref('qpWaveConfig'),
    fStatConfig: readPref('fStatConfig'),
    qpGraphConfig: readPref('qpGraphConfig'),
    fstConfig: readPref('fstConfig'),
    // Session memory for the target picker's MY SAMPLES / REFERENCE
    // switch on formalstats (true = MY SAMPLES, false = REFERENCE).
    // Default true preserves the pre-session-persistence behavior.
    // Session-scoped via vuex-persist so the user's chosen mode
    // survives a tab refresh but resets per session.
    targetFromRef: true,
    // Imputation jobs keyed by RAW sample name — latest job per sample.
    // Populated from queryFstatHistory (analysis_type='impute') when the
    // Samples modal opens and updated live via onPublishResult
    // subscriptions for each in-flight jobid.
    //
    //   { [sample]: { jobid, status, created_at, input_params, error_message } }
    //
    // Mutated via the generic `change` mutation. Not persisted in
    // jsonstrings.json — each open of the modal re-fetches and the live
    // state is the source of truth, so sessionStorage round-trips would
    // just risk stale data.
    imputationJobs: {}
}
data.init = JSON.stringify(data);
export const store = createStore({
    state() {
        return data
    },
    mutations: {
        change(state, props) {
            var key = Object.keys(props)[0];
            var val = props[key];
            val = jsonstrings[key] && typeof(val) == 'object' && key != 'pcacustomsamples' && key != 'plotInterestedSamples' ? Object.freeze(val) : val
            if ( jsonstrings[key] === -101122 ) {
            console.log('setting memory map', key, val);
            window.g25cache[key] = val;
            } else {
            state[key] = val;
            }
        },
        increment(state, props) {
            var key = Object.keys(props)[0];
            var val = props[key];
            state[key]++
        }
    },
    getters: {
        get: (state) => (prop) => {
            if ( jsonstrings[prop] === -123 ) {
            console.log('getting memory map', prop);
            return window.g25cache[prop]
            } else {
            let data = state[prop]
            return data
            }
        }
    },
    plugins: [vuexPersist.plugin]
})

// Bind the store reference so readPref/writePref can resolve the
// current userid for user-scoped prefs. Must happen before any
// subscribe hook that might call write/readPref.
_store = store

// Write-through to localStorage for any persisted preference that
// flows through the `change` mutation. Decoupled from the main
// vuex-persist session plugin so durable prefs survive tab close
// while the heavier session-snapshot behavior stays unaffected.
//
// The actual `localStorage.setItem` call is deferred off the main
// task so the synchronous mutation → subscriber chain doesn't
// stretch the current frame. Noticeable on el-switch toggles — a
// sync write lands in the same frame as the switch-ball animation
// and makes the transition feel jerky compared to non-persisted
// switches. Prefer `requestIdleCallback` (browser runs it when the
// main thread is idle) with a `setTimeout(0)` fallback for
// browsers/environments where `requestIdleCallback` isn't
// available (e.g. Safari < 16.4). Both yield to the event loop so
// the pending paint completes before the write fires.
const scheduleDeferred = (typeof requestIdleCallback === 'function')
    ? (cb) => requestIdleCallback(cb, { timeout: 250 })
    : (cb) => setTimeout(cb, 0)

store.subscribe((mutation, state) => {
    if (mutation.type !== 'change') return
    const payload = mutation.payload
    if (!payload || typeof payload !== 'object') return
    const writes = []
    for (const key of PERSISTED_KEYS) {
        if (key in payload) writes.push([key, state[key]])
    }
    if (!writes.length) return
    scheduleDeferred(() => {
        for (const [key, value] of writes) writePref(key, value)
    })
})

// Re-hydrate user-scoped prefs whenever `state.user` changes.
//
// User-scoped prefs seeded at module boot used the `default` value
// because no user was logged in yet. Once sign-in commits a user we
// need to load that user's stored values. Also covers sign-out (user
// becomes false → prefs reset to defaults) and user switching (one
// user signs out, another signs in).
//
// Commits go through the normal `change` mutation so the write-through
// hook fires too — which is a no-op here because we're writing the
// same value we just read from localStorage.
store.subscribe((mutation, state) => {
    if (mutation.type !== 'change') return
    if (!mutation.payload || !('user' in mutation.payload)) return
    for (const key of PERSISTED_KEYS) {
        if (PERSISTED_PREFS[key].scope !== 'user') continue
        store.commit('change', { [key]: readPref(key) })
    }
})

// Make replaceState leave PERSISTED_PREFS untouched.
//
// Nav.vue.js resets state on sign-in/sign-out via
// `$store.replaceState(JSON.parse(init))`. `init` is a snapshot of the
// store frozen at module boot, so any durable pref that was toggled
// mid-session gets reverted to its boot value each time a user signs
// in again — leaving the Vuex state out of sync with localStorage
// until a hard refresh re-runs the seed.
//
// Rather than making every replaceState caller know which keys are
// durable, we override replaceState here to splice the current
// PERSISTED_PREFS values into the incoming snapshot before handing
// it off to Vuex. One-liner semantics for callers: "persistent keys
// survive a state replace."
const _origReplaceState = store.replaceState.bind(store)
store.replaceState = (nextState) => {
    const merged = { ...nextState }
    for (const key of PERSISTED_KEYS) {
        merged[key] = store.state[key]
    }
    _origReplaceState(merged)
}
